/**
 * GeoLocationService - Service de géolocalisation
 * Utilise l'API du navigateur et une API de géocodage inverse
 * @class GeoLocationService
 */
import { ApiService } from './ApiService.js';

export class GeoLocationService {
    /**
     * Crée une instance de GeoLocationService
     */
    constructor() {
        // API de géocodage inverse gratuite
        this._reverseGeoApi = new ApiService('https://nominatim.openstreetmap.org');
        this._ipGeoApi = new ApiService('https://ipapi.co');
    }

    /**
     * Vérifie si la géolocalisation est supportée
     * @returns {boolean} True si supportée
     */
    isSupported() {
        return 'geolocation' in navigator;
    }

    /**
     * Obtient la position actuelle de l'utilisateur via le navigateur
     * @param {Object} options - Options de géolocalisation
     * @returns {Promise<GeolocationPosition>} Position
     */
    getCurrentPosition(options = {}) {
        return new Promise((resolve, reject) => {
            if (!this.isSupported()) {
                reject(new Error('Geolocation is not supported by this browser'));
                return;
            }

            const defaultOptions = {
                enableHighAccuracy: true,
                timeout: 10000,
                maximumAge: 300000 // 5 minutes cache
            };

            navigator.geolocation.getCurrentPosition(
                resolve,
                reject,
                { ...defaultOptions, ...options }
            );
        });
    }

    /**
     * Obtient le code pays à partir des coordonnées (requête dépendante)
     * @param {number} latitude - Latitude
     * @param {number} longitude - Longitude
     * @returns {Promise<Object>} Informations de localisation
     */
    async getCountryFromCoordinates(latitude, longitude) {
        try {
            const data = await this._reverseGeoApi.get('/reverse', {
                format: 'json',
                lat: latitude,
                lon: longitude,
                'accept-language': 'fr'
            });

            return {
                countryCode: data.address?.country_code?.toUpperCase() || null,
                country: data.address?.country || null,
                city: data.address?.city || data.address?.town || data.address?.village || null,
                displayName: data.display_name || null
            };
        } catch (error) {
            console.error('Error getting country from coordinates:', error);
            throw error;
        }
    }

    /**
     * Obtient la localisation basée sur l'adresse IP (fallback)
     * @returns {Promise<Object>} Informations de localisation
     */
    async getLocationFromIP() {
        try {
            const data = await this._ipGeoApi.get('/json/');

            return {
                countryCode: data.country_code || null,
                country: data.country_name || null,
                city: data.city || null,
                latitude: data.latitude || null,
                longitude: data.longitude || null,
                currency: data.currency || null
            };
        } catch (error) {
            console.error('Error getting location from IP:', error);
            throw error;
        }
    }

    /**
     * Méthode principale : obtient le pays de l'utilisateur
     * Essaie d'abord la géolocalisation du navigateur, puis l'IP comme fallback
     * @returns {Promise<Object>} Informations du pays
     */
    async getUserCountry() {
        try {
            // Tentative 1: Géolocalisation du navigateur
            if (this.isSupported()) {
                try {
                    const position = await this.getCurrentPosition();
                    const { latitude, longitude } = position.coords;
                    
                    // Requête dépendante : utilise les coordonnées pour obtenir le pays
                    const locationInfo = await this.getCountryFromCoordinates(latitude, longitude);
                    
                    return {
                        ...locationInfo,
                        latitude,
                        longitude,
                        source: 'geolocation'
                    };
                } catch (geoError) {
                    console.warn('Geolocation failed, falling back to IP:', geoError.message);
                }
            }

            // Tentative 2: Géolocalisation par IP
            const ipLocation = await this.getLocationFromIP();
            return {
                ...ipLocation,
                source: 'ip'
            };

        } catch (error) {
            console.error('All geolocation methods failed:', error);
            // Retourne France par défaut
            return {
                countryCode: 'FR',
                country: 'France',
                latitude: 48.8566,
                longitude: 2.3522,
                source: 'default'
            };
        }
    }
}
